import GestionArchivo from "../Archivo/GestionArchivo";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/Api";
import { usePermisos } from "../../context/PermisosContext";
import { isSuperAdmin } from "../../services/authUtils";
import {
  cancelarReemplazoDispositivo,
  iniciarReemplazoDispositivo,
  obtenerDetallesDispositivo,
  revelarClaveDispositivo,
  rotarClaveDispositivo,
} from "../../services/dispositivoService";
import { getApiError } from "../../services/apiError";
import { authorizeDeviceUpdate, cancelDeviceUpdate, getDeviceUpdates } from "../../services/firmwareService";

const formatearMac = (valor = "") => {
  const hexadecimal = valor.toUpperCase().replace(/[^0-9A-F]/g, "").slice(0, 12);
  return hexadecimal.match(/.{1,2}/g)?.join(":") || "";
};

const formatearFecha = (valor) =>
  valor ? new Date(valor).toLocaleString() : "Sin verificar";

const estiloFirmware = (compatible, detectado) => {
  if (!detectado) return "text-amber-300";
  return compatible ? "text-emerald-300" : "text-red-400";
};

const compararVersiones = (actual = "0", publicada = "0") => {
  const a = String(actual).split(".").map((x) => Number.parseInt(x, 10) || 0);
  const b = String(publicada).split(".").map((x) => Number.parseInt(x, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    if ((a[i] || 0) !== (b[i] || 0)) return (a[i] || 0) < (b[i] || 0) ? -1 : 1;
  }
  return 0;
};

const carpetaFirmware = (tipo = "") => {
  const valor = String(tipo).trim().toLowerCase();
  if (valor === "sensores") return "Sensores";
  if (valor === "efectores") return "Efectores";
  if (valor === "mixto") return "Mixto";
  return null;
};

const AdministrarDispositivos = () => {
  const { can, canEdit } = usePermisos();

  // Permisos efectivos
  const esSuper = useMemo(() => !!isSuperAdmin(), []);
  const esAdministrador = esSuper || String(localStorage.getItem("rol") || "").trim().toLowerCase() === "administrador";
  const puedeVer = esSuper || !!can?.("DISPOSITIVOS_VIEW");
  const puedeEditar = esSuper || !!canEdit?.("DISPOSITIVOS_EDIT");

  const [dispositivos, setDispositivos] = useState([]);
  const [estanques, setEstanques] = useState([]);
  const [mensaje, setMensaje] = useState("");
  const [claveNueva, setClaveNueva] = useState(null);
  const [seguridadIoT, setSeguridadIoT] = useState(null);
  const [passwordIoT, setPasswordIoT] = useState("");
  const [procesandoIoT, setProcesandoIoT] = useState(false);
  const [errorIoT, setErrorIoT] = useState("");
  const [claveIoTNoRecuperable, setClaveIoTNoRecuperable] = useState(false);
  const [detalles, setDetalles] = useState(null);
  const [cargandoDetalles, setCargandoDetalles] = useState(false);
  const [reemplazo, setReemplazo] = useState(null);
  const [procesandoReemplazo, setProcesandoReemplazo] = useState(false);
  const [comprobandoActualizaciones, setComprobandoActualizaciones] = useState(false);
  const [actualizaciones, setActualizaciones] = useState({});
  const [resumenActualizaciones, setResumenActualizaciones] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const dispositivosFiltrados = useMemo(() => {
    const q = busqueda.trim().toLocaleLowerCase("es");
    if (!q) return dispositivos;
    return dispositivos.filter((d) => [d.nombre, d.codigo, d.estanqueNombre, d.ip, d.tipoFirmwareRequerido,
      d.tipoFirmwareDetectado, d.versionFirmware, d.estaActivo ? "activo" : "inactivo"]
      .some((v) => String(v ?? "").toLocaleLowerCase("es").includes(q)));
  }, [dispositivos, busqueda]);

  // Edición por fila
  const [editandoId, setEditandoId] = useState(null);
  const [formRow, setFormRow] = useState({
    nombre: "",
    codigo: "",
    estanqueId: "",
    estaActivo: false,
    requierePassword: false,
    passwordPlano: "",
  });
  const [mostrarPass, setMostrarPass] = useState(false);

  const fetchDispositivos = useCallback(async () => {
    if (!puedeVer) return;
    try {
      // usamos solo endpoints existentes “mis-*”
      const res = await api.get("Dispositivos/mis-dispositivos");
      setDispositivos(res?.data || []);
    } catch (error) {
      console.error("❌ Error al cargar los dispositivos", error);
      setMensaje("❌ Error al cargar los dispositivos");
    }
  }, [puedeVer]);

  const fetchEstanques = useCallback(async () => {
    if (!puedeVer) return;
    try {
      const res = await api.get("Estanques/mis-estanques");
      setEstanques(res?.data || []);
    } catch (error) {
      console.error("❌ Error al cargar estanques", error);
    }
  }, [puedeVer]);

  const comprobarActualizaciones = async () => {
    if (!esAdministrador) return;
    setComprobandoActualizaciones(true);
    setMensaje("");
    try {
      const carpetas = ["Sensores", "Efectores", "Mixto"];
      const manifests = await Promise.all(
        carpetas.map(async (carpeta) => {
          const respuesta = await fetch(`/Firmware/${carpeta}/manifest.json?ts=${Date.now()}`, { cache: "no-store" });
          if (!respuesta.ok) throw new Error(`No se pudo consultar ${carpeta}`);
          return [carpeta, await respuesta.json()];
        })
      );
      const publicados = Object.fromEntries(manifests);
      const campanas = await getDeviceUpdates();
      const porDispositivo = Object.fromEntries(campanas.map((x) => [x.deviceId, x]));
      const resultado = {};
      dispositivos.forEach((d) => {
        const campana = porDispositivo[d.id];
        const carpeta = carpetaFirmware(d.tipoFirmwareRequerido);
        const publicada = carpeta ? publicados[carpeta]?.version : null;
        if (campana) {
          const texto = campana.authorized && !campana.online && campana.progress <= 5
            ? `Autorizada; esperando conexión del ESP32`
            : campana.required ? `Obligatoria ${campana.version}`
            : campana.canAuthorize ? `Opcional ${campana.version} disponible`
            : campana.authorizationStatus === "RequiereIntervencion" ? "Requiere intervención"
            : campana.authorizationStatus === "ReintentoProgramado" ? "Reintento programado"
            : `${campana.authorizationStatus}: ${campana.version}`;
          resultado[d.id] = { estado: campana.required ? "obligatoria" : "campana", texto, ...campana };
        } else if (!d.tipoFirmwareDetectado || !d.versionFirmware) {
          resultado[d.id] = { estado: "sin-verificar", texto: "Pendiente de comunicación", publicada };
        } else if (d.firmwareCompatible === false) {
          resultado[d.id] = { estado: "incompatible", texto: "Firmware incorrecto", publicada };
        } else if (publicada && compararVersiones(d.versionFirmware, publicada) < 0) {
          resultado[d.id] = { estado: "actualizar", texto: `Actualizar a ${publicada}`, publicada };
        } else {
          resultado[d.id] = { estado: "actualizado", texto: `Actualizado (${d.versionFirmware})`, publicada };
        }
      });
      setActualizaciones(resultado);
      const valores = Object.values(resultado);
      setResumenActualizaciones({
        total: valores.length,
        actualizar: valores.filter((x) => ["actualizar", "incompatible", "campana", "obligatoria"].includes(x.estado)).length,
        actualizados: valores.filter((x) => x.estado === "actualizado").length,
        pendientes: valores.filter((x) => x.estado === "sin-verificar").length,
      });
      setMensaje("✅ Comprobación de firmware completada.");
    } catch (error) {
      console.error("Error comprobando actualizaciones", error);
      setMensaje("❌ No se pudo consultar la versión publicada.");
    } finally {
      setComprobandoActualizaciones(false);
    }
  };

  const gestionarActualizacion = async (item, cancelar = false) => {
    setComprobandoActualizaciones(true); setMensaje("");
    try {
      if (cancelar) await cancelDeviceUpdate(item.deviceId, item.deploymentId);
      else {
        if (!window.confirm(`¿Autorizar la instalación de la versión ${item.version} en este ESP32?`)) return;
        await authorizeDeviceUpdate(item.deviceId, item.deploymentId);
      }
      await comprobarActualizaciones();
      setMensaje(cancelar ? "✅ Autorización cancelada." : "✅ Actualización autorizada para la próxima conexión del ESP32.");
    } catch (e) { setMensaje(`❌ ${getApiError(e, "No se pudo cambiar la autorización OTA.")}`); }
    finally { setComprobandoActualizaciones(false); }
  };

  const toggleEstado = useCallback(
    async (id) => {
      if (!puedeEditar) return;
      try {
        await api.put(`Dispositivos/toggle-activo/${id}`, {});
        fetchDispositivos();
      } catch (error) {
        console.error("❌ Error al cambiar estado del dispositivo", error);
        setMensaje("❌ Error al cambiar el estado");
      }
    },
    [puedeEditar, fetchDispositivos]
  );

  const abrirSeguridadIoT = (d) => {
    const dispositivo = dispositivos.find((item) => item.id === d.id) || d;
    if (!dispositivo.requierePassword) {
      setMensaje("❌ Configura primero una contraseña para el dispositivo.");
      return;
    }
    setSeguridadIoT(dispositivo);
    setDetalles(null);
    setPasswordIoT("");
    setErrorIoT("");
    setClaveIoTNoRecuperable(false);
  };

  const procesarClaveIoT = async (nueva) => {
    if (!passwordIoT) {
      setMensaje("❌ Escribe la contraseña del dispositivo.");
      return;
    }
    if (nueva && !window.confirm("La clave IoT anterior dejará de funcionar inmediatamente. ¿Generar una nueva?")) return;
    setProcesandoIoT(true);
    setErrorIoT("");
    try {
      const result = nueva
        ? await rotarClaveDispositivo(seguridadIoT.id, passwordIoT)
        : await revelarClaveDispositivo(seguridadIoT.id, passwordIoT);
      setClaveNueva({ ...result, nueva });
      setSeguridadIoT(null);
      setPasswordIoT("");
      setMensaje(nueva ? "✅ Nueva clave IoT generada." : "✅ Clave IoT recuperada.");
    } catch (error) {
      const detail = getApiError(error, nueva ? "No se pudo generar la clave." : "No se pudo mostrar la clave.");
      setErrorIoT(detail);
      if (!nueva && error?.response?.status === 409) setClaveIoTNoRecuperable(true);
      setMensaje(`❌ ${detail}`);
    } finally {
      setProcesandoIoT(false);
    }
  };

  const abrirDetalles = async (dispositivo) => {
    setCargandoDetalles(true);
    setMensaje("");
    try {
      setDetalles(await obtenerDetallesDispositivo(dispositivo.id));
    } catch (error) {
      setMensaje(`❌ ${getApiError(error, "No se pudieron cargar los detalles.")}`);
    } finally {
      setCargandoDetalles(false);
    }
  };

  const iniciarReemplazo = async () => {
    if (!detalles || !puedeEditar) return;
    if (!window.confirm("El dispositivo entrará en mantenimiento: se pausará la automatización y se ordenará apagar sus salidas. Prepara una alternativa para los equipos esenciales y retira el ESP32 anterior. Se conservará la configuración. ¿Continuar?")) return;
    setProcesandoReemplazo(true);
    try {
      const data = await iniciarReemplazoDispositivo(detalles.id);
      setReemplazo({ ...data, dispositivoId: detalles.id, nombre: detalles.nombre });
      setDetalles(null);
      await fetchDispositivos();
    } catch (error) {
      setMensaje(`❌ ${getApiError(error, "No se pudo iniciar el reemplazo.")}`);
    } finally {
      setProcesandoReemplazo(false);
    }
  };

  const urlInstaladorFirmware = (tipoFirmware) => {
    const tipo = String(tipoFirmware || "").trim().toLowerCase();
    return ["sensores", "efectores", "mixto"].includes(tipo)
      ? `/dashboard/admin?tab=Configuraciones&sub=firmware&tipo=${tipo}`
      : null;
  };

  const cancelarReemplazo = async () => {
    if (!reemplazo) return;
    setProcesandoReemplazo(true);
    try {
      await cancelarReemplazoDispositivo(reemplazo.dispositivoId);
      setReemplazo(null);
      setMensaje("✅ Reemplazo cancelado.");
      await fetchDispositivos();
    } catch (error) {
      setMensaje(`❌ ${getApiError(error, "No se pudo cancelar el reemplazo.")}`);
    } finally {
      setProcesandoReemplazo(false);
    }
  };

  useEffect(() => {
    fetchDispositivos();
    fetchEstanques();
  }, [fetchDispositivos, fetchEstanques]);

  const comenzarEdicion = (d) => {
    setEditandoId(d.id);
    setFormRow({
      nombre: d.nombre || "",
      codigo: formatearMac(d.codigo),
      estanqueId: d.estanqueId || "",
      estaActivo: !!d.estaActivo,
      requierePassword: !!d.requierePassword,
      passwordPlano: "",
    });
    setMostrarPass(false);
    setMensaje("");
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setFormRow({
      nombre: "",
      codigo: "",
      estanqueId: "",
      estaActivo: false,
      requierePassword: false,
      passwordPlano: "",
    });
    setMostrarPass(false);
  };

  const guardarFila = useCallback(async () => {
    setMensaje("");

    if (!puedeEditar) return;

    if (formRow.requierePassword && formRow.passwordPlano && formRow.passwordPlano.length < 4) {
        setMensaje("❌ La nueva contraseña debe tener al menos 4 caracteres.");
        return;
    }

    try {
      // 1) Actualizar campos generales
      await api.put(`Dispositivos/actualizar/${editandoId}`, {
        nombre: formRow.nombre,
        estanqueId: formRow.estanqueId ? Number(formRow.estanqueId) : null,
        estaActivo: !!formRow.estaActivo,
      });

      // 2) Actualizar seguridad
      await api.put(`Dispositivos/${editandoId}/password`, {
        requierePassword: !!formRow.requierePassword,
        // Vacío conserva la contraseña existente; sólo se reemplaza si se escribe una nueva.
        passwordPlano: formRow.requierePassword ? formRow.passwordPlano || null : null,
      });

      setMensaje("✅ Dispositivo actualizado");
      await fetchDispositivos();
      cancelarEdicion();
    } catch (error) {
      console.error("❌ Error al actualizar dispositivo", error);
      setMensaje("❌ Error al actualizar el dispositivo");
    }
  }, [puedeEditar, formRow, editandoId, fetchDispositivos]);

  if (!puedeVer) {
    return (
      <div className="p-6 bg-neutral-950 text-neutral-300 rounded-lg max-w-4xl mx-auto">
        No tienes permisos para ver Dispositivos.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 bg-neutral-900 text-white rounded-lg">
      <GestionArchivo tipo="dispositivos" puedeEditar={puedeEditar} onChanged={fetchDispositivos} />
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg sm:text-xl text-green-400 font-bold">Administrar Dispositivos</h3>
        <div className="flex flex-wrap items-center gap-2">
          {esAdministrador && <button onClick={comprobarActualizaciones} disabled={comprobandoActualizaciones} className="px-4 py-2 rounded bg-cyan-700 hover:bg-cyan-600 disabled:opacity-50">{comprobandoActualizaciones ? "Comprobando…" : "Comprobar actualizaciones"}</button>}
          {esSuper && <span className="text-xs border border-emerald-500/60 px-2 py-1 rounded text-emerald-300">Superadministrador</span>}
        </div>
      </div>

      {resumenActualizaciones && <div className="mb-4 grid grid-cols-2 md:grid-cols-4 gap-2 text-sm"><div className="rounded border border-white/10 bg-black/30 p-3">Revisados<br/><strong className="text-xl">{resumenActualizaciones.total}</strong></div><div className="rounded border border-emerald-500/30 bg-emerald-950/20 p-3">Actualizados<br/><strong className="text-xl text-emerald-300">{resumenActualizaciones.actualizados}</strong></div><div className="rounded border border-amber-500/30 bg-amber-950/20 p-3">Necesitan actualizar<br/><strong className="text-xl text-amber-300">{resumenActualizaciones.actualizar}</strong></div><div className="rounded border border-neutral-500/30 bg-neutral-950/30 p-3">Sin verificar<br/><strong className="text-xl text-neutral-300">{resumenActualizaciones.pendientes}</strong></div></div>}

      {mensaje && (
        <div className={`mb-3 text-sm ${mensaje.startsWith("✅") ? "text-green-400" : "text-red-500"}`}>
          {mensaje}
        </div>
      )}

      <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)}
        placeholder="Buscar por nombre, código, estanque, IP, firmware o estado…"
        className="w-full mb-4 p-2.5 rounded bg-neutral-800 border border-cyan-700/40 text-white" />

      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="min-w-[1100px] w-full table-auto">
          <thead>
            <tr className="bg-green-600 text-white text-xs sm:text-sm">
              <th className="p-2 sm:p-3 text-left">Nombre</th>
              <th className="p-2 sm:p-3 text-left">Código</th>
              <th className="p-2 sm:p-3 text-left">Estanque</th>
              <th className="p-2 sm:p-3 text-left">Firmware</th>
              <th className="p-2 sm:p-3 text-left">IP</th>
              <th className="p-2 sm:p-3 text-left">Activo</th>
              <th className="p-2 sm:p-3 text-left">Requiere contraseña</th>
              <th className="p-2 sm:p-3 text-left">Contraseña (nueva)</th>
              <th className="p-2 sm:p-3 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {dispositivosFiltrados.map((d) => {
              const enEdicion = editandoId === d.id;
              return (
                <tr
                  key={d.id}
                  className="bg-gray-800/80 hover:bg-gray-800 text-left text-xs sm:text-sm align-top"
                >
                  <td className="p-2 sm:p-3">
                    {enEdicion ? (
                      <input
                        value={formRow.nombre}
                        onChange={(e) => setFormRow((s) => ({ ...s, nombre: e.target.value }))}
                        className="w-full px-2 py-1 rounded bg-neutral-700 border border-neutral-600 text-white"
                        disabled={!puedeEditar}
                      />
                    ) : (
                      d.nombre
                    )}
                  </td>

                  <td className="p-2 sm:p-3">
                    {enEdicion ? (
                      <input
                        value={formRow.codigo}
                        className="w-full px-2 py-1 rounded bg-neutral-800 border border-neutral-700 text-gray-300 cursor-not-allowed"
                        disabled
                        title="La MAC solo se cambia mediante Reemplazar ESP32"
                      />
                    ) : (
                      formatearMac(d.codigo)
                    )}
                  </td>

                  <td className="p-2 sm:p-3">
                    {enEdicion ? (
                      <select
                        value={formRow.estanqueId ?? ""}
                        onChange={(e) => setFormRow((s) => ({ ...s, estanqueId: e.target.value }))}
                        className="w-full px-2 py-1 rounded bg-neutral-700 border border-neutral-600 text-white"
                        disabled title="Usa Trasladar en Archivo y reutilización para conservar el historial."
                      >
                        <option value="">— Selecciona —</option>
                        {estanques.map((es) => (
                          <option key={es.id} value={es.id}>
                            {es.nombre}
                          </option>
                        ))}
                      </select>
                    ) : d.estanqueNombre || `ID ${d.estanqueId}`}
                  </td>

                  <td className={`p-2 sm:p-3 ${estiloFirmware(d.firmwareCompatible, d.tipoFirmwareDetectado)}`}>
                    <div className="font-semibold">{d.tipoFirmwareRequerido || "Pendiente"}</div>
                    <div className="text-[11px] opacity-80">
                      {d.tipoFirmwareDetectado
                        ? `${d.tipoFirmwareDetectado} ${d.versionFirmware || ""}`
                        : "Pendiente de verificar"}
                    </div>
                    {actualizaciones[d.id] && <div className="mt-1 space-y-1"><div className={`text-[11px] font-bold ${actualizaciones[d.id].estado === "actualizado" ? "text-emerald-300" : actualizaciones[d.id].authorizationStatus === "RequiereIntervencion" ? "text-red-300" : actualizaciones[d.id].estado === "sin-verificar" ? "text-neutral-300" : "text-amber-300"}`}>{actualizaciones[d.id].texto}</div>{actualizaciones[d.id].deploymentId && <><div className="flex items-center gap-2" title={`Progreso OTA: ${actualizaciones[d.id].progress || 0}%`}><div className="h-2 flex-1 overflow-hidden rounded-full bg-neutral-700"><div className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-emerald-400 transition-all duration-700" style={{width:`${actualizaciones[d.id].progress || 0}%`}} /></div><span className="text-[10px] text-cyan-100">{actualizaciones[d.id].progress || 0}%</span></div>{!actualizaciones[d.id].online && <div className="rounded border border-amber-500/30 bg-amber-950/30 px-2 py-1 text-[10px] text-amber-100">Sin conexión · última comunicación: {formatearFecha(actualizaciones[d.id].lastConnection)}</div>}</>}{actualizaciones[d.id].canAuthorize && <button disabled={comprobandoActualizaciones} onClick={()=>gestionarActualizacion(actualizaciones[d.id])} className="rounded border border-emerald-400 bg-emerald-900/50 px-2 py-1 text-[11px] font-bold text-emerald-100">Autorizar actualización</button>}{actualizaciones[d.id].canCancel && <button disabled={comprobandoActualizaciones} onClick={()=>gestionarActualizacion(actualizaciones[d.id],true)} className="rounded border border-neutral-500 px-2 py-1 text-[11px] text-neutral-200">Cancelar autorización</button>}</div>}
                  </td>

                  <td className="p-2 sm:p-3">
                    {enEdicion ? (
                      <input
                        value={d.ip || "No reportada"}
                        disabled
                        className="w-full px-2 py-1 rounded bg-neutral-800 border border-neutral-700 text-gray-300 cursor-not-allowed"
                        title="IP reportada por el dispositivo (solo lectura)"
                      />
                    ) : (
                      d.ip || "No reportada"
                    )}
                  </td>

                  <td className="p-2 sm:p-3">
                    {enEdicion ? (
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!formRow.estaActivo}
                          onChange={(e) => setFormRow((s) => ({ ...s, estaActivo: e.target.checked }))}
                          disabled={!puedeEditar}
                        />
                        <span>{formRow.estaActivo ? "Activo" : "Inactivo"}</span>
                      </label>
                    ) : d.estaActivo ? (
                      "Activo"
                    ) : (
                      "Inactivo"
                    )}
                  </td>

                  <td className="p-2 sm:p-3">
                    {enEdicion ? (
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!formRow.requierePassword}
                          onChange={(e) =>
                            setFormRow((s) => ({
                              ...s,
                              requierePassword: e.target.checked,
                              passwordPlano: e.target.checked ? s.passwordPlano : "",
                            }))
                          }
                          disabled={!puedeEditar}
                        />
                        <span>{formRow.requierePassword ? "Sí" : "No"}</span>
                      </label>
                    ) : d.requierePassword ? (
                      "Sí"
                    ) : (
                      "No"
                    )}
                  </td>

                  <td className="p-2 sm:p-3">
                    {enEdicion ? (
                      formRow.requierePassword ? (
                        <div className="flex">
                          <input
                            type={mostrarPass ? "text" : "password"}
                            value={formRow.passwordPlano}
                            onChange={(e) => setFormRow((s) => ({ ...s, passwordPlano: e.target.value }))}
                            placeholder="Vacío = conservar actual"
                            className="flex-1 px-2 py-1 rounded-l bg-neutral-700 border border-neutral-600 text-white"
                            disabled={!puedeEditar}
                          />
                          <button
                            type="button"
                            onClick={() => setMostrarPass((v) => !v)}
                            className="px-2 rounded-r bg-neutral-700 border border-l-0 border-neutral-600"
                            title={mostrarPass ? "Ocultar" : "Mostrar"}
                            disabled={!puedeEditar}
                          >
                            {mostrarPass ? "🙈" : "👁️"}
                          </button>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )
                    ) : d.requierePassword ? (
                      "••••••"
                    ) : (
                      "—"
                    )}
                  </td>

                  <td className="p-2 sm:p-3">
                    {enEdicion ? (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          onClick={guardarFila}
                          className={`px-3 py-1.5 rounded text-white ${
                            puedeEditar ? "bg-green-600 hover:bg-green-700" : "bg-neutral-700 cursor-not-allowed"
                          }`}
                          disabled={!puedeEditar}
                        >
                          Guardar
                        </button>
                        <button
                          onClick={cancelarEdicion}
                          className="px-3 py-1.5 rounded text-white bg-gray-600 hover:bg-gray-500"
                        >
                          Cancelar
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          onClick={() => comenzarEdicion(d)}
                          className={`px-3 py-1.5 rounded text-black ${
                            puedeEditar ? "bg-yellow-400 hover:bg-yellow-500" : "bg-neutral-700 text-white"
                          }`}
                          disabled={!puedeEditar}
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => abrirDetalles(d)}
                          className="px-3 py-1.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50"
                          disabled={cargandoDetalles}
                        >
                          Detalles
                        </button>
                        <button
                          onClick={() => toggleEstado(d.id)}
                          className={`px-3 py-1.5 rounded text-white ${
                            puedeEditar
                              ? d.estaActivo
                                ? "bg-red-600 hover:bg-red-700"
                                : "bg-green-600 hover:bg-green-700"
                              : "bg-neutral-700 cursor-not-allowed"
                          }`}
                          disabled={!puedeEditar}
                        >
                          {d.estaActivo ? "Desactivar" : "Activar"}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}

            {dispositivos.length === 0 && (
              <tr>
                <td className="p-4 text-center text-gray-400 text-sm" colSpan={9}>
                  No hay dispositivos registrados.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {seguridadIoT && <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"><div className="max-w-lg w-full bg-neutral-900 border border-cyan-600 rounded-xl p-5 space-y-4"><h4 className="text-xl font-bold text-cyan-300">Clave IoT de {seguridadIoT.nombre}</h4><p className="text-sm text-neutral-300">{claveIoTNoRecuperable ? "La clave anterior no se puede recuperar. Genera una nueva para continuar." : "Escribe la contraseña del dispositivo para visualizar la clave o generar una nueva."}</p><input autoFocus type="password" value={passwordIoT} onChange={(e) => setPasswordIoT(e.target.value)} placeholder="Contraseña del dispositivo" className="w-full bg-black border border-neutral-600 rounded p-3"/>{errorIoT && <div className="rounded-lg border border-amber-500/40 bg-amber-950/40 p-3 text-sm text-amber-200">{errorIoT}</div>}<div className="flex flex-wrap gap-2">{!claveIoTNoRecuperable && <button disabled={procesandoIoT} onClick={() => procesarClaveIoT(false)} className="bg-cyan-700 px-4 py-2 rounded disabled:opacity-50">Ver clave actual</button>}<button disabled={procesandoIoT} onClick={() => procesarClaveIoT(true)} className="bg-amber-600 text-black px-4 py-2 rounded disabled:opacity-50">{claveIoTNoRecuperable ? "Generar nueva clave recuperable" : "Obtener nueva clave IoT"}</button><button disabled={procesandoIoT} onClick={() => setSeguridadIoT(null)} className="bg-neutral-700 px-4 py-2 rounded">Cancelar</button></div></div></div>}
      {claveNueva && <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"><div className="max-w-xl w-full bg-neutral-900 border border-amber-500 rounded-xl p-5 space-y-4"><h4 className="text-xl font-bold text-amber-300">{claveNueva.nueva ? "Nueva clave IoT" : "Clave IoT actual"}</h4><p>Configúrala en el ESP32 como cabecera <code>{claveNueva.header}</code>. La clave permanece cifrada en el backend.</p><textarea readOnly value={claveNueva.apiKey} className="w-full bg-black border border-neutral-600 rounded p-3 font-mono" rows={3}/><div className="flex gap-2"><button onClick={() => navigator.clipboard?.writeText(claveNueva.apiKey)} className="bg-cyan-700 px-4 py-2 rounded">Copiar</button><button onClick={() => setClaveNueva(null)} className="bg-amber-600 text-black px-4 py-2 rounded">Cerrar</button></div></div></div>}
      {detalles && <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto"><div className="max-w-2xl w-full bg-neutral-900 border border-indigo-500 rounded-xl p-5 space-y-4"><div className="flex justify-between gap-4"><div><h4 className="text-xl font-bold text-indigo-300">Detalles de {detalles.nombre}</h4><p className="text-sm text-neutral-400">MAC {formatearMac(detalles.mac)} · {detalles.estanque}</p></div><span className={`font-bold ${estiloFirmware(detalles.firmwareCompatible, detalles.tipoFirmwareDetectado)}`}>{detalles.estadoFirmware}</span></div><div className="grid sm:grid-cols-2 gap-3 text-sm"><div className="bg-black/40 rounded p-3"><div className="text-neutral-400">Firmware requerido</div><div className="text-lg font-bold">{detalles.tipoFirmwareRequerido}</div></div><div className="bg-black/40 rounded p-3"><div className="text-neutral-400">Firmware detectado</div><div className="text-lg font-bold">{detalles.tipoFirmwareDetectado || "Pendiente"} {detalles.versionFirmware || ""}</div></div><div className="bg-black/40 rounded p-3"><div className="text-neutral-400">Última verificación</div><div>{formatearFecha(detalles.fechaUltimaVerificacionFirmware)}</div></div><div className="bg-black/40 rounded p-3"><div className="text-neutral-400">Última comunicación</div><div>{formatearFecha(detalles.fechaUltimaConexion)}</div></div><div className="bg-black/40 rounded p-3"><div className="text-neutral-400">Conexiones conservadas</div><div>{detalles.cantidadSensores} sensores · {detalles.cantidadEfectores} efectores</div></div><div className="bg-black/40 rounded p-3"><div className="text-neutral-400">IP reportada</div><div>{detalles.ip || "No reportada"}</div></div></div><details><summary>Detalles técnicos y conexiones</summary><button className="bg-neutral-700 px-3 py-2 rounded" onClick={() => navigator.clipboard?.writeText(detalles.mac)}>Copiar MAC</button><p>IP: {detalles.ip || 'No reportada'} · MAC: {formatearMac(detalles.mac)}</p>{(detalles.sensores || []).map(s => <p key={`s${s.id}`}>Sensor {s.nombre}: señal {s.pinPrincipal || '—'}, RX {s.pinRx || '—'}, TX {s.pinTx || '—'}</p>)}{(detalles.efectores || []).map(e => <p key={`e${e.id}`}>Efector {e.nombre}: salida {e.pinActivacion}</p>)}</details><div className="flex flex-wrap gap-2"><a className="bg-cyan-700 px-3 py-2 rounded" href={`/dashboard/admin?tab=Configuraciones&sub=firmware&fw=configurar&dispositivoId=${detalles.id}`}>Cambiar Wi-Fi</a>{puedeEditar && <><a className="bg-neutral-700 px-3 py-2 rounded" href={`/dashboard/admin?tab=Sensores&sub=crear&dispositivoId=${detalles.id}`}>Agregar sensor</a><a className="bg-neutral-700 px-3 py-2 rounded" href={`/dashboard/admin?tab=Efectores&sub=crear&dispositivoId=${detalles.id}`}>Agregar efector</a></>}</div>{detalles.enMantenimiento && <div><p>Mantenimiento: automatización pausada. Verifica cableado, lecturas (la simulación sigue disponible) y órdenes manuales desde Efectores. La prueba física requiere comprobar el equipo en el lugar.</p><button disabled={!puedeEditar} className="bg-amber-600 px-3 py-2 rounded" onClick={async () => { if(!window.confirm('¿Verificaste el cableado y el funcionamiento físico en el lugar? Al aceptar se reanudará la automatización.')) return; try { await api.post(`/Dispositivos/${detalles.id}/reemplazo/aceptar`,{cableadoVerificado:true,funcionamientoFisicoVerificado:true}); const {data}=await api.get(`/Dispositivos/${detalles.id}/detalles`);setDetalles(data);setMensaje('✅ Reemplazo aceptado; automatización reanudada.'); } catch(e) { setMensaje(`❌ ${getApiError(e,'No se pudo aceptar el reemplazo')}`); } }}>Aceptar pruebas y finalizar mantenimiento</button></div>}{detalles.firmwareCompatible === false && <div className="border border-red-500 bg-red-950/40 text-red-200 rounded p-3">Este dispositivo requiere firmware {detalles.tipoFirmwareRequerido}, pero reportó {detalles.tipoFirmwareDetectado} {detalles.versionFirmware}.</div>}<div className="flex flex-wrap gap-2"><button disabled={!puedeEditar || procesandoReemplazo} onClick={iniciarReemplazo} className="bg-amber-500 hover:bg-amber-400 text-black px-4 py-2 rounded font-semibold disabled:opacity-50">Reemplazar ESP32</button>{esAdministrador && <button disabled={!puedeEditar} onClick={() => abrirSeguridadIoT(detalles)} className="bg-cyan-700 hover:bg-cyan-600 px-4 py-2 rounded disabled:opacity-50">Clave IoT</button>}{urlInstaladorFirmware(detalles.tipoFirmwareRequerido) && <a href={urlInstaladorFirmware(detalles.tipoFirmwareRequerido)} className="bg-cyan-700 hover:bg-cyan-600 px-4 py-2 rounded">Instalar firmware correcto</a>}<button onClick={() => setDetalles(null)} className="bg-neutral-700 px-4 py-2 rounded">Cerrar</button></div></div></div>}
      {reemplazo && <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-4"><div className="max-w-xl w-full bg-neutral-900 border border-amber-500 rounded-xl p-5 space-y-4"><h4 className="text-xl font-bold text-amber-300">Reemplazar ESP32 de {reemplazo.nombre}</h4><p>Instala el firmware <strong>{reemplazo.tipoFirmwareRequerido}</strong> en el ESP32 nuevo. Abre su portal Wi-Fi e introduce este código temporal:</p><div className="text-center text-3xl tracking-[0.25em] font-mono font-bold bg-black border border-amber-500 rounded p-4 select-all">{reemplazo.token}</div><p className="text-sm text-neutral-300">Vence: {formatearFecha(reemplazo.expiraUtc)}. La vinculación conservará sensores, efectores, pines y automatizaciones. Después verifica el cableado, lecturas y salidas en Detalles; acepta las pruebas para reanudar la automatización.</p><div className="flex flex-wrap gap-2"><button onClick={() => navigator.clipboard?.writeText(reemplazo.token)} className="bg-cyan-700 px-4 py-2 rounded">Copiar código</button>{urlInstaladorFirmware(reemplazo.tipoFirmwareRequerido) && <a href={urlInstaladorFirmware(reemplazo.tipoFirmwareRequerido)} className="bg-emerald-700 px-4 py-2 rounded">Instalar {reemplazo.tipoFirmwareRequerido}</a>}<button disabled={procesandoReemplazo} onClick={cancelarReemplazo} className="bg-red-700 px-4 py-2 rounded disabled:opacity-50">Cancelar reemplazo</button><button onClick={() => setReemplazo(null)} className="bg-neutral-700 px-4 py-2 rounded">Cerrar</button></div></div></div>}
    </div>
  );
};

export default AdministrarDispositivos;
